import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { dashboardClient } from '../../../api/client';
import {
  GetActivityPageSummaryGrainEnum,
  GetActivityPageSummaryModeEnum,
  GetActivityPageSummaryPeriodEnum,
  QueryActivityPageDetail,
  QueryActivityPageSummary,
} from '../../../api/schema';
import { RootState } from '../../store';
import { ActivityState, detailKey, initialState, summaryKey } from './activityInterface';

/**
 * The window list, or an explicit window. The drill-down asks for the second
 * form so it can draw the same charts a level finer; either way the response
 * has the same shape and is cached under the arguments that produced it.
 */
export const fetchActivitySummary = createAsyncThunk(
  'activity/fetchSummary',
  async (args: {
    period?: GetActivityPageSummaryPeriodEnum;
    mode?: GetActivityPageSummaryModeEnum;
    tz: string;
    from?: string;
    to?: string;
    grain?: GetActivityPageSummaryGrainEnum;
  }) => dashboardClient.getActivityPageSummary(args.period ?? 'week', args.mode ?? 'trailing', args.tz, undefined, args.from, args.to, args.grain),
);

export const fetchActivityDetail = createAsyncThunk('activity/fetchDetail', async ({ from, to, tz }: { from: string; to: string; tz: string }) =>
  dashboardClient.getActivityPageDetail(from, to, tz),
);

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchActivitySummary.pending, (state: ActivityState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), fetchActivitySummary: true };
    });
    builder.addCase(fetchActivitySummary.fulfilled, (state: ActivityState, action: AnyAction) => {
      state.fetching = { ...state.fetching, fetchActivitySummary: false };
      const { period, mode, from, to } = action.meta.arg;
      const key = from && to ? summaryKey(from, to) : summaryKey(period ?? 'week', mode ?? 'trailing');
      state.summary[key] = action.payload.data.result || {};
    });
    builder.addCase(fetchActivitySummary.rejected, (state: ActivityState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, fetchActivitySummary: false };
    });

    builder.addCase(fetchActivityDetail.pending, (state: ActivityState) => {
      state.error = undefined;
      state.fetching = { ...(state.fetching || {}), fetchActivityDetail: true };
    });
    builder.addCase(fetchActivityDetail.fulfilled, (state: ActivityState, action: AnyAction) => {
      state.fetching = { ...state.fetching, fetchActivityDetail: false };
      const { from, to } = action.meta.arg;
      state.detail[detailKey(from, to)] = action.payload.data.result || {};
    });
    builder.addCase(fetchActivityDetail.rejected, (state: ActivityState, action: AnyAction) => {
      state.error = action.error;
      state.fetching = { ...state.fetching, fetchActivityDetail: false };
    });
  },
});

export const getActivitySummaryFor =
  (period: string, mode: string) =>
  (state: RootState): QueryActivityPageSummary | undefined =>
    state.activity.summary[summaryKey(period, mode)];

export const getActivityDetailFor =
  (from: string, to: string) =>
  (state: RootState): QueryActivityPageDetail | undefined =>
    state.activity.detail[detailKey(from, to)];

// Undefined counts as fetching, so the first render shows skeletons rather than
// an empty page.
export const getActivitySummaryFetching = (state: RootState): boolean => state.activity.fetching?.fetchActivitySummary !== false;
export const getActivityDetailFetching = (state: RootState): boolean => state.activity.fetching?.fetchActivityDetail !== false;

export default activitySlice.reducer;
